import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import {
  OPS_TOOL_ADD_BILL_FEE_SUMMARIES,
  OPS_TOOL_SETTLEMENT_BILLS,
  OPS_TOOL_SETTLEMENT_FEE_ITEMS,
} from '../graphql/opsToolSettlementGraphql';
import { useOpsToolScenarioFilters } from '../hooks/useOpsToolScenarioFilters';

type BillRow = {
  id: string;
  settlementClientName: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  totalAmount: number;
  status: number;
  isCanceled: boolean | null;
  feeSummaries: {
    id: string;
    feeItemName: string | null;
    feeItemCode: string | null;
    amount: number;
  }[];
};

const formatAmount = (amount: number) => `${(amount / 100).toFixed(2)} 元 (${amount} 分)`;

const BillFeeSummaryTab: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const {
    businessScenarioCode,
    setBusinessScenarioCode,
    billType,
    setBillType,
    billTypeOptions,
    settlementClientId,
    setSettlementClientId,
    periodRange,
    setPeriodRange,
    scenarioOptions,
    scenariosLoading,
    clientOptions,
    clientsLoading,
    buildFilters,
  } = useOpsToolScenarioFilters();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addMode, setAddMode] = useState<'SINGLE' | 'BATCH' | 'ALL'>('BATCH');
  const [targetBillId, setTargetBillId] = useState<string | null>(null);
  const [addForm] = Form.useForm();

  const { data: feeItemsData } = useQuery(OPS_TOOL_SETTLEMENT_FEE_ITEMS, {
    skip: !businessScenarioCode,
    variables: { businessScenarioCode: businessScenarioCode! },
  });

  const [loadBills, { data: billsData, loading: billsLoading, refetch: refetchBills }] =
    useLazyQuery(OPS_TOOL_SETTLEMENT_BILLS);
  const [addSummaries, { loading: addingSummaries }] = useMutation(OPS_TOOL_ADD_BILL_FEE_SUMMARIES);

  const mainFeeItemOptions = useMemo(
    () =>
      (feeItemsData?.opsToolSettlementFeeItems ?? []).map(
        (item: { id: string; code: string; name: string }) => ({
          value: Number(item.id),
          label: `${item.name} (${item.code})`,
        })
      ),
    [feeItemsData]
  );

  const rows: BillRow[] = useMemo(
    () =>
      (billsData?.opsToolSettlementBills?.content ?? []).map((row: BillRow) => ({
        ...row,
        id: String(row.id),
      })),
    [billsData]
  );

  const totalElements = billsData?.opsToolSettlementBills?.totalElements ?? 0;

  const fetchBills = (nextPage = page, nextSize = pageSize) => {
    if (businessScenarioCode == null || billType == null) return;
    loadBills({
      variables: {
        filters: buildFilters(),
        pageRequest: { page: nextPage, size: nextSize },
      },
    });
  };

  useEffect(() => {
    if (businessScenarioCode == null || billType == null) return;
    fetchBills(1, pageSize);
    setPage(1);
    setSelectedRowKeys([]);
  }, [businessScenarioCode, billType, settlementClientId, periodRange]);

  const openAddModal = (mode: 'SINGLE' | 'BATCH' | 'ALL', billId?: string) => {
    setAddMode(mode);
    setTargetBillId(billId ?? null);
    addForm.setFieldsValue({ settlementFeeItemId: undefined, amount: 0 });
    setAddModalOpen(true);
  };

  const handleAddSummaries = async () => {
    if (businessScenarioCode == null) return;
    try {
      const values = await addForm.validateFields();
      const filterValues = buildFilters();
      const input: Record<string, unknown> = {
        addMode,
        businessScenarioCode,
        billType: filterValues.billType,
        settlementClientId: filterValues.settlementClientId,
        periodStartFrom: filterValues.periodStartFrom ?? null,
        periodEndTo: filterValues.periodEndTo ?? null,
        settlementFeeItemId: values.settlementFeeItemId,
        amount: 0,
      };
      if (addMode === 'SINGLE') input.billIds = [String(targetBillId)]; // ID 超出JS安全整数，以字符串传输
      else if (addMode === 'BATCH') input.billIds = selectedRowKeys.map(k => String(k));

      const result = await addSummaries({ variables: { input } });
      const payload = result.data?.opsToolAddBillFeeSummaries;
      if (!payload?.success) {
        message.error(payload?.message ?? '新增失败');
        return;
      }
      message.success(
        `新增成功：创建 ${payload.createdCount ?? 0} 条，跳过 ${payload.skippedCount ?? 0} 条（已存在相同费用项汇总）`
      );
      setAddModalOpen(false);
      setSelectedRowKeys([]);
      refetchBills();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const columns: ColumnsType<BillRow> = [
    { title: '账单 ID', dataIndex: 'id', width: 100 },
    {
      title: '客户',
      dataIndex: 'settlementClientName',
      width: 180,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '账单开始',
      dataIndex: 'periodStart',
      width: 170,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '账单结束',
      dataIndex: 'periodEnd',
      width: 170,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '账单总金额',
      dataIndex: 'totalAmount',
      width: 160,
      render: (v: number) => formatAmount(v),
    },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => <Tag>{v}</Tag> },
    {
      title: '费用项汇总',
      dataIndex: 'feeSummaries',
      render: (items: BillRow['feeSummaries']) =>
        items?.length ? (
          <Space size={[4, 4]} wrap>
            {items.map(item => (
              <Tag key={item.id}>
                {item.feeItemName ?? item.feeItemCode}：{formatAmount(item.amount)}
              </Tag>
            ))}
          </Space>
        ) : (
          <span className="text-gray-400">无汇总</span>
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) =>
        canOperate ? (
          <Button type="link" size="small" onClick={() => openAddModal('SINGLE', record.id)}>
            新增汇总
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Space wrap className="mb-4">
        <Select
          style={{ width: 220 }}
          placeholder="业务场景"
          loading={scenariosLoading}
          value={businessScenarioCode}
          options={scenarioOptions}
          onChange={setBusinessScenarioCode}
        />
        <Select
          style={{ width: 160 }}
          placeholder="账单类型"
          value={billType}
          options={billTypeOptions}
          onChange={setBillType}
        />
        <Select
          style={{ width: 260 }}
          placeholder="客户"
          loading={clientsLoading}
          value={settlementClientId}
          options={clientOptions}
          onChange={setSettlementClientId}
          showSearch
          optionFilterProp="label"
        />
        <DatePicker.RangePicker value={periodRange} onChange={v => setPeriodRange(v)} allowEmpty={[true, true]} />
        <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); setSelectedRowKeys([]); fetchBills(1, pageSize); }}>
          查询
        </Button>
        {canOperate && (
          <>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              icon={<PlusOutlined />}
              onClick={() => openAddModal('BATCH')}
            >
              批量新增 ({selectedRowKeys.length})
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => openAddModal('ALL')}>
              全部账单新增
            </Button>
          </>
        )}
      </Space>

      <Table
        rowKey="id"
        loading={billsLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1300 }}
        rowSelection={canOperate ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
        pagination={{
          current: page,
          pageSize,
          total: totalElements,
          showSizeChanger: true,
          onChange: (p, s) => { setPage(p); setPageSize(s); fetchBills(p, s); },
        }}
      />

      <Modal
        title={
          addMode === 'SINGLE' ? '新增单个账单费用项汇总' :
          addMode === 'BATCH' ? '批量新增账单费用项汇总' : '全部账单新增费用项汇总'
        }
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddSummaries}
        confirmLoading={addingSummaries}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="settlementFeeItemId"
            label="主费用项"
            rules={[{ required: true, message: '请选择主费用项' }]}
          >
            <Select options={mainFeeItemOptions} placeholder="请选择主费用项" showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            name="amount"
            label="汇总金额（分）"
            extra="运维工具新增汇总金额固定为 0"
          >
            <InputNumber style={{ width: '100%' }} precision={0} disabled min={0} max={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BillFeeSummaryTab;
